import type { TypedComponentRendering } from '@core/jss-models';
import { getFieldValue } from '@core/jss-utils';

import type {
  OrderContent,
  ScOrderFields,
} from './order-fulfillment-container.model';

export function scRenderingContentMapper(
  rendering: TypedComponentRendering<ScOrderFields>
): OrderContent {
  const enableAtoCheck = getFieldValue(rendering, 'EnableATOCheck', false);

  return {
    cardContent: rendering.fields.CardContent,
    cardTitle: getFieldValue(rendering, 'CardTitle', ''),
    continueAsNewCustomerButtonText: getFieldValue(
      rendering,
      'ContinueAsNewCustomerButtonText',
      ''
    ),
    redirectToMijnButton: rendering.fields.RedirectToMijnButton,
    termsAndConditionsFormGroupLabel: getFieldValue(
      rendering,
      'TermsAndConditionsFormGroupLabel',
      ''
    ),
    atoConsentFormGroupLabel: getFieldValue(
      rendering,
      'ATOConsentFormGroupLabel',
      ''
    ),
    atoConsentText: getFieldValue(rendering, 'ATOConsentText', ''),
    enableNewOfferCardTitles: getFieldValue(
      rendering,
      'EnableNewOfferCardTitles',
      false
    ),
    enableBusinessFields: getFieldValue(
      rendering,
      'EnableBusinessFields',
      false
    ),
    enablePersonalFields: getFieldValue(
      rendering,
      'EnablePersonalFields',
      false
    ),
    enableBusinessCheckbox: getFieldValue(
      rendering,
      'EnableBusinessCheckbox',
      false
    ),
    enableMoveDateInThePast: getFieldValue(
      rendering,
      'EnableMoveDateInThePast',
      false
    ),
    enableAtoCheck,
    startDateOptionControlEnabled: getFieldValue(
      rendering,
      'StartDateOptionControlEnabled',
      false
    ),
    labelsShownAboveFields: getFieldValue(
      rendering,
      'LabelsShownAboveFields',
      false
    ),
    enableCrossSellStep: getFieldValue(rendering, 'EnableCrossSellStep', false),
    splitIbanText: getFieldValue(rendering, 'splitIbanText', false),
    salutationOptions: rendering.fields.salutationOptions,
    residenceOrWorkplaceOptions: rendering.fields.residenceOrWorkplaceOptions,
    exclusivelyCorporateUsageOptions:
      rendering.fields.exclusivelyCorporateUsageOptions,
    showChangeSupplyAddressLink: getFieldValue(
      rendering,
      'ShowChangeSupplyAddressLink',
      false
    ),
    changeSupplyAddressLinkText: getFieldValue(
      rendering,
      'ChangeSupplyAddressLinkText',
      ''
    ),
    changeSupplyAddressModalTitle: getFieldValue(
      rendering,
      'ChangeSupplyAddressModalTitle',
      ''
    ),
    changeSupplyAddressModalBody: rendering.fields.changeSupplyAddressModalBody,
    changeSupplyAddressModalPrimaryButtonText: getFieldValue(
      rendering,
      'ChangeSupplyAddressModalPrimaryButtonText',
      ''
    ),
    commercialTooltip: getFieldValue(rendering, 'CommercialTooltip', ''),
    // Remove the conditional once the ATO consent is released (1/1/2026)
    confirmationSummaryBefore:
      rendering.fields[
        enableAtoCheck
          ? 'ConfirmationSummaryATOBefore'
          : 'confirmationSummaryBefore'
      ],
    confirmationSummaryAfter:
      rendering.fields[
        enableAtoCheck
          ? 'ConfirmationSummaryATOAfter'
          : 'confirmationSummaryAfter'
      ],
    confirmationSummaryTooltip: getFieldValue(
      rendering,
      enableAtoCheck
        ? 'ConfirmationSummaryATOTooltip'
        : 'confirmationSummaryTooltip',
      ''
    ),
    consentsTextBefore: rendering.fields.ConsentsTextBefore,
    consentsTextLink: getFieldValue(rendering, 'ConsentsTextLink', ''),
    consentsTextAfter: rendering.fields.ConsentsTextAfter,
    consentsMessageTitle: getFieldValue(rendering, 'ConsentsMessageTitle', ''),
    consentsMessageBody: getFieldValue(rendering, 'ConsentsMessageBody', ''),
    consentsMessageButton: getFieldValue(
      rendering,
      'ConsentsMessageButton',
      ''
    ),
    residentialFunctionModalTitle: getFieldValue(
      rendering,
      'ResidentialFunctionModalTitle',
      ''
    ),
    residentialFunctionModalBody: rendering.fields.residentialFunctionModalBody,
    residentialFunctionModalPrimaryButtonText: getFieldValue(
      rendering,
      'ResidentialFunctionModalPrimaryButtonText',
      ''
    ),
    residentialFunctionModalSecondaryButtonText: getFieldValue(
      rendering,
      'ResidentialFunctionModalSecondaryButtonText',
      ''
    ),
    personalCardTitle: rendering.fields.PersonalCardTitle,
    personalCardSubTitle: getFieldValue(rendering, 'PersonalCardSubTitle', ''),
    personalCardSubmitButton: getFieldValue(
      rendering,
      'PersonalCardSubmitButton',
      ''
    ),
    businessFieldsTitle: getFieldValue(rendering, 'BusinessFieldsTitle', ''),
    companyNameLabel: getFieldValue(rendering, 'CompanyNameLabel', ''),
    companyNamePlaceholder: getFieldValue(
      rendering,
      'CompanyNamePlaceholder',
      ''
    ),
    companyNameRequiredError: getFieldValue(
      rendering,
      'CompanyNameRequiredError',
      ''
    ),
    kvkNumberLabel: getFieldValue(rendering, 'KvkNumberLabel', ''),
    kvkNumberPlaceholder: getFieldValue(rendering, 'KvkNumberPlaceholder', ''),
    kvkNumberRequiredError: getFieldValue(
      rendering,
      'KvkNumberRequiredError',
      ''
    ),
    salutationLabel: getFieldValue(rendering, 'SalutationLabel', ''),
    genderRequiredError: getFieldValue(rendering, 'GenderRequiredError', ''),
    initialsLabel: getFieldValue(rendering, 'InitialsLabel', ''),
    initialsPlaceholder: getFieldValue(rendering, 'InitialsPlaceholder', ''),
    initialsRequiredError: getFieldValue(
      rendering,
      'InitialsRequiredError',
      ''
    ),
    insertionLabel: getFieldValue(rendering, 'InsertionLabel', ''),
    insertionPlaceholder: getFieldValue(rendering, 'InsertionPlaceholder', ''),
    surnameLabel: getFieldValue(rendering, 'SurnameLabel', ''),
    surnamePlaceholder: getFieldValue(rendering, 'SurnamePlaceholder', ''),
    surnameRequiredError: getFieldValue(rendering, 'SurnameRequiredError', ''),
    birthdateLabel: getFieldValue(rendering, 'BirthdateLabel', ''),
    birthdatePlaceholder: getFieldValue(rendering, 'BirthdatePlaceholder', ''),
    birthdateRequiredError: getFieldValue(
      rendering,
      'BirthdateRequiredError',
      ''
    ),
    businessClientLabel: getFieldValue(rendering, 'BusinessClientLabel', ''),
    businessClientTooltip: getFieldValue(
      rendering,
      'BusinessClientTooltip',
      ''
    ),
    businessClientMessageTitle: getFieldValue(
      rendering,
      'BusinessClientMessageTitle',
      ''
    ),
    businessClientMessageBody: rendering.fields.BusinessClientMessageBody,
    contactDetailsLabel: getFieldValue(rendering, 'ContactDetailsLabel', ''),
    emailDisclosure: getFieldValue(rendering, 'EmailDisclosure', ''),
    emailLabel: getFieldValue(rendering, 'EmailLabel', ''),
    emailPlaceholder: getFieldValue(rendering, 'EmailPlaceholder', ''),
    emailTooltip: getFieldValue(rendering, 'EmailTooltip', ''),
    emailRequiredError: getFieldValue(rendering, 'EmailRequiredError', ''),
    emailCheckLabel: getFieldValue(rendering, 'EmailCheckLabel', ''),
    emailCheckPlaceholder: getFieldValue(
      rendering,
      'EmailCheckPlaceholder',
      ''
    ),
    phoneLabel: getFieldValue(rendering, 'PhoneLabel', ''),
    phonePlaceholder: getFieldValue(rendering, 'PhonePlaceholder', ''),
    phoneTooltip: getFieldValue(rendering, 'PhoneTooltip', ''),
    phoneRequiredError: getFieldValue(rendering, 'PhoneRequiredError', ''),
    emailValidationError: getFieldValue(rendering, 'EmailValidationError', ''),
    addressCardTitle: rendering.fields.AddressCardTitle,
    addressCardSubtitle: getFieldValue(rendering, 'AddressCardSubtitle', ''),
    addressCardSubmitButton: getFieldValue(
      rendering,
      'AddressCardSubmitButton',
      ''
    ),
    addressCardSubmitButtonWithCrossSell: getFieldValue(
      rendering,
      'AddressCardSubmitButtonWithCrossSell',
      ''
    ),
    supplyAddressTitle: getFieldValue(rendering, 'SupplyAddressTitle', ''),
    correspondenceAddressLabel: getFieldValue(
      rendering,
      'CorrespondenceAddressLabel',
      ''
    ),
    correspondenceAddressTitle: getFieldValue(
      rendering,
      'CorrespondenceAddressTitle',
      ''
    ),
    careOfFullNameLabel: getFieldValue(rendering, 'CareOfFullNameLabel', ''),
    careOfFullNamePlaceholder: getFieldValue(
      rendering,
      'CareOfFullNamePlaceholder',
      ''
    ),
    postalCodeLabel: getFieldValue(rendering, 'PostalCodeLabel', ''),
    postalCodePlaceholder: getFieldValue(
      rendering,
      'PostalCodePlaceholder',
      ''
    ),
    houseNumberLabel: getFieldValue(rendering, 'HouseNumberLabel', ''),
    houseNumberPlaceholder: getFieldValue(
      rendering,
      'HouseNumberPlaceholder',
      ''
    ),
    residenceOrWorkplaceText: getFieldValue(
      rendering,
      'ResidenceOrWorkplaceText',
      ''
    ),
    residenceOrWorkplaceLabel: getFieldValue(
      rendering,
      'ResidenceOrWorkplaceLabel',
      ''
    ),
    residenceOrWorkplaceTooltip: getFieldValue(
      rendering,
      'ResidenceOrWorkplaceTooltip',
      ''
    ),
    residenceOrWorkplaceRequiredError: getFieldValue(
      rendering,
      'ResidenceOrWorkplaceRequiredError',
      ''
    ),
    inswitchText: getFieldValue(rendering, 'InswitchText', ''),
    inswitchLabel: getFieldValue(rendering, 'InswitchLabel', ''),
    transitionDateLabel: getFieldValue(rendering, 'TransitionDateLabel', ''),
    transitionDatePlaceholder: getFieldValue(
      rendering,
      'TransitionDatePlaceholder',
      ''
    ),
    transitionDateRequiredError: getFieldValue(
      rendering,
      'TransitionDateRequiredError',
      ''
    ),
    inhouseTermLabel: getFieldValue(rendering, 'InhouseTermLabel', ''),
    inhouseTermTooltip: getFieldValue(rendering, 'InhouseTermTooltip', ''),
    moveDateInPastInfoMessage: rendering.fields.MoveDateInPastInfoMessage,
    noStartDateOptionChosenErrorMessage: getFieldValue(
      rendering,
      'NoStartDateOptionChosenErrorMessage',
      ''
    ),
    earliestPossibleStartDateRadioButtonLabel: getFieldValue(
      rendering,
      'EarliestPossibleStartDateRadioButtonLabel',
      ''
    ),
    customStartDateRadioButtonLabel: getFieldValue(
      rendering,
      'CustomStartDateRadioButtonLabel',
      ''
    ),
    helpText: rendering.fields.HelpText,
    situationOtherSupplierInfoMessageButtonLink:
      rendering.fields.SituationOtherSupplierInfoMessageButtonLink,
    situationNoSupplierInswitchText: getFieldValue(
      rendering,
      'SituationNoSupplierInswitchText',
      ''
    ),
    dropdownLabel: getFieldValue(rendering, 'DropdownLabel', ''),
    dropdownPlaceholder: getFieldValue(rendering, 'DropdownPlaceholder', ''),
    dropdownSelectionRequiredError: getFieldValue(
      rendering,
      'DropdownSelectionRequiredError',
      ''
    ),
    situationNoSupplierLabel: getFieldValue(
      rendering,
      'SituationNoSupplierLabel',
      ''
    ),
    situationNoSupplierDatePickerTooltip: getFieldValue(
      rendering,
      'SituationNoSupplierDatePickerTooltip',
      ''
    ),
    situationOtherSupplierLabel: getFieldValue(
      rendering,
      'SituationOtherSupplierLabel',
      ''
    ),
    situationOtherSupplierInfoMessage:
      rendering.fields.SituationOtherSupplierInfoMessage,
    situationOtherSupplierDatePickerTooltip: getFieldValue(
      rendering,
      'SituationOtherSupplierDatePickerTooltip',
      ''
    ),
    situationRetentionLabel: getFieldValue(
      rendering,
      'SituationRetentionLabel',
      ''
    ),
    situationRetentionInfoMessage:
      rendering.fields.SituationRetentionInfoMessage,
    situationRetentionInfoMessageButtonLink:
      rendering.fields.SituationRetentionInfoMessageButtonLink,
    situationMoveOtherSupplierLabel: getFieldValue(
      rendering,
      'SituationMoveOtherSupplierLabel',
      ''
    ),
    situationMoveOtherSupplierInfoMessage:
      rendering.fields.SituationMoveOtherSupplierInfoMessage,
    situationMoveOtherSupplierDatePickerTooltip: getFieldValue(
      rendering,
      'SituationMoveOtherSupplierDatePickerTooltip',
      ''
    ),
    situationMoveLabel: getFieldValue(rendering, 'SituationMoveLabel', ''),
    situationMoveInfoMessage: rendering.fields.SituationMoveInfoMessage,
    situationMoveInfoMessageButtonLink:
      rendering.fields.SituationMoveInfoMessageButtonLink,
    situationExtraAddressLabel: getFieldValue(
      rendering,
      'SituationExtraAddressLabel',
      ''
    ),
    situationExtraAddressInfoMessage:
      rendering.fields.SituationExtraAddressInfoMessage,
    situationSummaryLabel: getFieldValue(
      rendering,
      'SituationSummaryLabel',
      ''
    ),
    situationMoveOtherSupplierDatePickerInfoMessage: getFieldValue(
      rendering,
      'SituationMoveOtherSupplierDatePickerInfoMessage',
      ''
    ),
    situationOtherSupplierDatePickerInfoMessage: getFieldValue(
      rendering,
      'SituationOtherSupplierDatePickerInfoMessage',
      ''
    ),
    situationMoveOtherSupplierInswitchText: getFieldValue(
      rendering,
      'SituationMoveOtherSupplierInswitchText',
      ''
    ),
    situationOtherSupplierInswitchText: getFieldValue(
      rendering,
      'SituationOtherSupplierInswitchText',
      ''
    ),
    paymentCardTitle: rendering.fields.PaymentCardTitle,
    paymentCardSubtitle: getFieldValue(rendering, 'PaymentCardSubtitle', ''),
    paymentCardSubmitButton: getFieldValue(
      rendering,
      'PaymentCardSubmitButton',
      ''
    ),
    ibanText: rendering.fields.IbanText,
    ibanLabel: getFieldValue(rendering, 'IbanLabel', ''),
    ibanPlaceholder: getFieldValue(rendering, 'IbanPlaceholder', ''),
    ibanRequiredError: getFieldValue(rendering, 'IbanRequiredError', ''),
    ibanBlacklistedError: rendering.fields.IbanBlacklistedError,
    ibanTextMobile: { value: getFieldValue(rendering, 'ibanTextMobile', '') },
    ibanTextDesktop: { value: getFieldValue(rendering, 'ibanTextDesktop', '') },
    checkCardTitle: getFieldValue(rendering, 'CheckCardTitle', ''),
    checkCardSubtitle: getFieldValue(rendering, 'CheckCardSubtitle', ''),
    checkCardSubmitButton: getFieldValue(
      rendering,
      'CheckCardSubmitButton',
      ''
    ),
    acceptConditionsTextBefore: getFieldValue(
      rendering,
      'AcceptConditionsTextBefore',
      ''
    ),
    acceptConditionsTextAfter: getFieldValue(
      rendering,
      'AcceptConditionsTextAfter',
      ''
    ),
    acceptConditionsTextDynamicContract: getFieldValue(
      rendering,
      'AcceptConditionsTextDynamicContract',
      ''
    ),
    generalTermsAndConditions: getFieldValue(
      rendering,
      'GeneralTermsAndConditions',
      ''
    ),
    qualityCriteria: getFieldValue(rendering, 'QualityCriteria', ''),
    productConditions: getFieldValue(rendering, 'ProductConditions', ''),
    campaignConditions: getFieldValue(rendering, 'CampaignConditions', ''),
    acceptConditionsRequiredError: getFieldValue(
      rendering,
      'AcceptConditionsRequiredError',
      ''
    ),
    atoConsentRequiredError: getFieldValue(
      rendering,
      'ATOConsentRequiredError',
      ''
    ),
    crossSellDesktopOnly: getFieldValue(
      rendering,
      'CrossSellDesktopOnly',
      false
    ),
    crossSellCardTitle: getFieldValue(rendering, 'CrossSellCardTitle', ''),
    crossSellCardSubtitle: getFieldValue(
      rendering,
      'CrossSellCardSubtitle',
      ''
    ),
    crossSellCardSubmitButton: getFieldValue(
      rendering,
      'CrossSellCardSubmitButton',
      ''
    ),
    crossSellProduct: rendering.fields.CrossSellProduct,
    crossSellInfoModalLink: getFieldValue(
      rendering,
      'CrossSellInfoModalLink',
      ''
    ),
    crossSellNoProductSelectedTitle: getFieldValue(
      rendering,
      'CrossSellNoProductSelectedTitle',
      ''
    ),
    crossSellNoProductSelectedSubtitle: getFieldValue(
      rendering,
      'CrossSellNoProductSelectedSubtitle',
      ''
    ),
    crossSellMonthlyShort: getFieldValue(
      rendering,
      'CrossSellMonthlyShort',
      ''
    ),
    crossSellMonthlyLong: getFieldValue(rendering, 'CrossSellMonthlyLong', ''),
    crossSellAddLabel: getFieldValue(rendering, 'CrossSellAddLabel', ''),
    crossSellAddedLabel: getFieldValue(rendering, 'CrossSellAddedLabel', ''),
    crossSellRejectLabel: getFieldValue(rendering, 'CrossSellRejectLabel', ''),
    crossSellInputRequiredError: getFieldValue(
      rendering,
      'CrossSellInputRequiredError',
      ''
    ),
    crossSellUseNewDesign: getFieldValue(
      rendering,
      'CrossSellUseNewDesign',
      false
    ),
    crossSellCardContentTitle: getFieldValue(
      rendering,
      'CrossSellCardContentTitle',
      ''
    ),
    crossSellNoOptionLabel: getFieldValue(
      rendering,
      'CrossSellNoOptionLabel',
      ''
    ),
    thuisvoordeelAddonEnabled: getFieldValue(
      rendering,
      'thuisvoordeelAddonEnabled',
      false
    ),
    thuisvoordeelCardContentTitle: getFieldValue(
      rendering,
      'thuisvoordeelCardContentTitle',
      ''
    ),
    thuisvoordeelNoOptionLabel: getFieldValue(
      rendering,
      'thuisvoordeelNoOptionLabel',
      ''
    ),
    thuisvoordeelYesOptionLabel: getFieldValue(
      rendering,
      'thuisvoordeelYesOptionLabel',
      ''
    ),
    thuisvoordeelInfoModalLink: getFieldValue(
      rendering,
      'thuisvoordeelInfoModalLink',
      ''
    ),
    thuisvoordeelModalTitle: getFieldValue(
      rendering,
      'thuisvoordeelModalTitle',
      ''
    ),
    thuisvoordeelModalContent: getFieldValue(
      rendering,
      'thuisvoordeelModalContent',
      ''
    ),
    thuisvoordeelPrimaryButtonText: getFieldValue(
      rendering,
      'thuisvoordeelPrimaryButtonText',
      ''
    ),
    thuisvoordeelSecondaryButtonText: getFieldValue(
      rendering,
      'thuisvoordeelSecondaryButtonText',
      ''
    ),
    thuisvoordeelSummaryTitle: getFieldValue(
      rendering,
      'thuisvoordeelSummaryTitle',
      ''
    ),
    thuisvoordeelSummarySubtitle: getFieldValue(
      rendering,
      'thuisvoordeelSummarySubtitle',
      ''
    ),
    emailConsentsModalTitle: getFieldValue(
      rendering,
      'EmailConsentsModalTitle',
      ''
    ),
    emailConsentsModalBody: rendering.fields.EmailConsentsModalBody,
    emailConsentsModalEnergyAdviceCheckbox: getFieldValue(
      rendering,
      'EmailConsentsModalEnergyAdviceCheckbox',
      ''
    ),
    emailConsentsModalSustainabilityCheckbox: getFieldValue(
      rendering,
      'EmailConsentsModalSustainabilityCheckbox',
      ''
    ),
    emailConsentsModalPrimaryButton: getFieldValue(
      rendering,
      'EmailConsentsModalPrimaryButton',
      ''
    ),
    emailConsentsModalSecondaryButton: getFieldValue(
      rendering,
      'EmailConsentsModalSecondaryButton',
      ''
    ),
    confirmationLabelAfterSubmitButton: getFieldValue(
      rendering,
      'confirmationLabelAfterSubmitButton',
      ''
    ),
    telemarketingConsentsTitle: getFieldValue(
      rendering,
      'telemarketingConsentsTitle',
      ''
    ),
    telemarketingContractAdvice: getFieldValue(
      rendering,
      'telemarketingContractAdvice',
      ''
    ),
    telemarketingContractAdviceTooltip: getFieldValue(
      rendering,
      'telemarketingContractAdviceTooltip',
      ''
    ),
    telemarketingSustainability: getFieldValue(
      rendering,
      'telemarketingSustainability',
      ''
    ),
    telemarketingSustainabilityTooltip: getFieldValue(
      rendering,
      'telemarketingSustainabilityTooltip',
      ''
    ),
    enableCustomerTypeStep: getFieldValue(
      rendering,
      'EnableCustomerTypeStep',
      false
    ),
    customerTypeCardTitle: getFieldValue(
      rendering,
      'CustomerTypeCardTitle',
      ''
    ),
    customerTypeCardSubtitle: getFieldValue(
      rendering,
      'CustomerTypeCardSubtitle',
      ''
    ),
    customerTypeCompanySizeQuestion: getFieldValue(
      rendering,
      'CustomerTypeCompanySizeQuestion',
      ''
    ),
    customerTypeCompanySizeOptions:
      rendering.fields.CustomerTypeCompanySizeOptions,
    customerTypeSegmentQuestion: getFieldValue(
      rendering,
      'CustomerTypeSegmentQuestion',
      ''
    ),
    customerTypeSegmentOptions: rendering.fields.CustomerTypeSegmentOptions,
    customerTypeInfoTitle: getFieldValue(
      rendering,
      'CustomerTypeInfoTitle',
      ''
    ),
    customerTypeInfoLink: getFieldValue(rendering, 'CustomerTypeInfoLink', ''),
    customerTypeSubmitButtonText: getFieldValue(
      rendering,
      'CustomerTypeSubmitButtonText',
      ''
    ),
    customerTypeModalTitle: getFieldValue(
      rendering,
      'CustomerTypeModalTitle',
      ''
    ),
    customerTypeModalBody: rendering.fields.CustomerTypeModalBody,
    customerTypeModalButtonText: getFieldValue(
      rendering,
      'CustomerTypeModalButtonText',
      ''
    ),
    customerTypeFormError: getFieldValue(
      rendering,
      'CustomerTypeFormError',
      ''
    ),
    customerTypeNavigateToButtonText: getFieldValue(
      rendering,
      'CustomerTypeNavigateToButtonText',
      ''
    ),
    customerTypeSummaryText: rendering.fields.CustomerTypeSummaryText,
    customerTypeSummaryOptions: rendering.fields.CustomerTypeSummaryOptions,
  };
}
